import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  Badge,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaHospital,
  FaFlask,
  FaShip,
  FaDollarSign,
  FaCube,
  FaWater,
  FaClock,
  FaMicrochip,
} from 'react-icons/fa';
import Footer from '../Footer';

const MultiphaseAcousticsBubbleScreen = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const tableBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>High-Fidelity 3D Multiphase Acoustics: Acoustic Waves Through Dense Bubble Clouds | SRS Blog</title>
        <meta name="description" content="Simulate acoustic wave propagation through dense bubble clouds in the browser: watch a pressure wave scatter and interact with thousands of gas bubbles in 3D." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/multiphase-acoustics-bubble-screen" />
        <meta property="og:title" content="High-Fidelity 3D Multiphase Acoustics: Acoustic Waves Through Dense Bubble Clouds | SRS Blog" />
        <meta property="og:description" content="Simulate acoustic wave propagation through dense bubble clouds in the browser: watch a pressure wave scatter and interact with thousands of gas bubbles in 3D." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/multiphase-acoustics-bubble-screen" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="High-Fidelity 3D Multiphase Acoustics: Acoustic Waves Through Dense Bubble Clouds | SRS Blog" />
        <meta name="twitter:description" content="Simulate acoustic wave propagation through dense bubble clouds in the browser: watch a pressure wave scatter and interact with thousands of gas bubbles in 3D." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="purple" fontSize="md" mb={4} px={3} py={1}>
              Case Study
            </Badge>
            <Heading 
              as="h1"
              size="2xl"
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              High-Fidelity 3D Multiphase Acoustics
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              Simulating Acoustic Wave Propagation Through Dense Bubble Clouds
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              Some of the hardest flow problems to test in the lab are the ones that mix acoustics, liquids, and thousands of tiny gas bubbles. With NumericalAI, you can run these scenarios directly in the browser and see how an incoming pressure wave travels through, scatters, and interacts with a dense bubble cloud.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              The visualization below shows a 3D bubble screen inside water — every blue shape is a tracked bubble responding to the acoustic pulse. This is the kind of detail that helps teams design quieter systems, better dampers, and more efficient underwater or medical devices.
            </Text>
          </Box>

          {/* Main Image */}
          <Box 
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            shadow="2xl"
            border="1px"
            borderColor={borderColor}
          >
            <Image 
              src={require('../../img/blogs/3D_lagrange_bubblescreen_Void_fraction_time_0_33s_NumericalAI.jpg')}
              alt="3D Lagrange Bubble Screen Simulation"
              w="100%"
              objectFit="contain"
              bg="gray.100"
            />
            <Box p={6} bg={sectionBg}>
              <Text fontSize="lg" color="gray.700" fontStyle="italic" textAlign="center" mb={2}>
                <strong>Figure 1:</strong> 3D view of bubble cloud exposed to an acoustic source — void fraction distribution at t = 0.33s
              </Text>
              <Text fontSize="md" color="gray.600" textAlign="center">
                The visualization shows the spatial distribution of gas volume fraction, with individual bubbles tracked using a Lagrangian approach while the surrounding liquid flow is captured with Eulerian methods.
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Simulation Overview */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Overview
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Domain Configuration</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Domain Size:</Text>
                      <Text fontWeight="semibold">0.5m × 0.3m × 0.3m</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">200 × 120 × 120 cells</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Cells:</Text>
                      <Text fontWeight="semibold">2.88 million</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Bubble Count:</Text>
                      <Text fontWeight="semibold">~3,500 bubbles</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaWater} boxSize={6} color="blue.500" />
                    <Heading size="md" color="gray.800">Physical Parameters</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Fluid:</Text>
                      <Text fontWeight="semibold">Water (ρ = 998 kg/m³)</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Gas:</Text>
                      <Text fontWeight="semibold">Air (ρ = 1.2 kg/m³)</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Bubble Diameter:</Text>
                      <Text fontWeight="semibold">2-5 mm</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Acoustic Frequency:</Text>
                      <Text fontWeight="semibold">5 kHz</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaClock} boxSize={6} color="orange.500" />
                    <Heading size="md" color="gray.800">Temporal Settings</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Simulation Time:</Text>
                      <Text fontWeight="semibold">0.5 seconds</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Step:</Text>
                      <Text fontWeight="semibold">5 × 10⁻⁶ s</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Steps:</Text>
                      <Text fontWeight="semibold">100,000</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">CFL Number:</Text>
                      <Text fontWeight="semibold">0.3</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaMicrochip} boxSize={6} color="purple.500" />
                    <Heading size="md" color="gray.800">Computational Cost</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Wall-clock Time (estimated):</Text>
                      <Text fontWeight="semibold">~16min</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">GPU Used:</Text>
                      <Text fontWeight="semibold">NVIDIA A100</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Memory Usage:</Text>
                      <Text fontWeight="semibold">~37 GB</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Speedup vs 1 CPU:</Text>
                      <Text fontWeight="semibold" color="green.600">~36×</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>
          </Box>

          <Divider />

          {/* Technical Approach */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Technical Approach
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Hybrid Eulerian-Lagrangian Framework
                </Heading>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" mb={4}>
                  This simulation employs a sophisticated two-way coupled approach that captures the best of both worlds:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Eulerian Phase (Continuous Liquid)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        The water phase is solved on a fixed Cartesian grid using compressible Navier-Stokes equations with acoustic wave propagation. A high-order WENO scheme captures sharp pressure gradients.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Lagrangian Phase (Dispersed Bubbles)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Each bubble is tracked as a discrete particle with 6-DOF dynamics. The Rayleigh-Plesset equation governs radial oscillations in response to local pressure changes, while drag, lift, and added-mass forces determine translational motion.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Two-Way Coupling</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Bubbles receive pressure and velocity information from the Eulerian grid, while their volume and momentum are projected back onto the grid using a particle-source-in-cell (PSI-Cell) method. This bidirectional exchange captures realistic wave scattering and attenuation.
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Acoustic Source Modeling
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  A planar acoustic source is positioned at x = 0, emitting a sinusoidal pressure wave with amplitude of 50 kPa and frequency of 5 kHz. The source uses a soft-start ramp over 3 cycles to avoid spurious numerical oscillations. The bubble screen is positioned 0.15m downstream, creating a dense scattering region with void fractions up to 15%.
                </Text>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Boundary Conditions
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Inlet (x = 0):</Text>
                    <Text color="gray.600">Prescribed pressure wave</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Outlet (x = 0.5m):</Text>
                    <Text color="gray.600">Non-reflecting boundary</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Lateral (y, z):</Text>
                    <Text color="gray.600">Periodic boundaries</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={2}>Bubble Surface:</Text>
                    <Text color="gray.600">Free-slip, compressible interface</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Key Results */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Key Results & Findings
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg="blue.50" p={6} borderRadius="xl" border="2px" borderColor="blue.200">
                <Heading size="md" color="blue.800" mb={4}>
                  Acoustic Attenuation Performance
                </Heading>
                <Text fontSize="md" color="blue.900" lineHeight="tall" mb={4}>
                  The bubble screen achieved a <strong>23 dB reduction</strong> in transmitted acoustic energy at 5 kHz, with peak attenuation occurring at the bubble resonance frequency (~4.8 kHz for 3mm diameter bubbles).
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
                  <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">23 dB</Text>
                    <Text fontSize="sm" color="gray.600">Sound Reduction</Text>
                  </Box>
                  <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">~65%</Text>
                    <Text fontSize="sm" color="gray.600">Energy Dissipated</Text>
                  </Box>
                  <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">4.8 kHz</Text>
                    <Text fontSize="sm" color="gray.600">Peak Frequency</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Bubble Dynamics Observations
                </Heading>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Radial Oscillations:</strong> Bubbles near resonance exhibited volume changes up to 35%, significantly enhancing scattering efficiency
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Collective Effects:</strong> Dense packing (void fraction 15%) created cooperative oscillations that amplified attenuation by ~30% compared to isolated bubbles
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Bubble Drift:</strong> Acoustic radiation pressure caused net bubble displacement of ~2cm downstream over the 0.5s simulation
                    </Text>
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* What NumericalAI Delivers */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              What NumericalAI Delivers for This Problem
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Full 3D resolution</strong> — capture spatial wave patterns and bubble distribution effects impossible in 2D
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Individual bubble tracking</strong> — thousands of bubbles with independent dynamics, not statistical averaging
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Time-accurate acoustics</strong> — resolve wave propagation with microsecond precision for frequency-dependent studies
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>GPU-accelerated solve</strong> — 45× faster than CPU, turning multi-day simulations into same-day results
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Cloud execution</strong> — no local HPC infrastructure needed, run from anywhere with a web browser
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Where This Matters */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Applications
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaShip} boxSize={8} color="brand.500" />
                  <Heading size="md" color="gray.800">
                    Marine & Underwater Defense
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Design and optimize bubble curtain systems for acoustic signature control in submarines, surface vessels, and sonar countermeasures. Predict attenuation performance across frequency bands without expensive sea trials.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaIndustry} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Energy & Process Engineering
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Understand cavitation damage in pumps and turbines, design gas-liquid contactors with acoustic monitoring, and optimize bubble column reactors. Prevent equipment failure through predictive multiphase flow modeling.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaHospital} boxSize={8} color="red.500" />
                  <Heading size="md" color="gray.800">
                    Medical Ultrasound & Therapy
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Model ultrasound contrast agents, focused ultrasound surgery (FUS) with microbubbles, and cavitation-enhanced drug delivery. Virtual testing accelerates device development and treatment planning.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFlask} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    Research & Development
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Explore fundamental physics of bubble-acoustic interactions, validate reduced-order models, and develop new attenuation concepts. High-fidelity data generation for AI/ML training datasets.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Business Value */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} />
                <Heading size="lg">Business Value</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                NumericalAI turns what used to be a specialist, HPC-only workflow into a repeatable SaaS experience: upload the setup, run, visualize, and share — all without installing a solver or maintaining a cluster.
              </Text>
              <Text fontSize="lg" lineHeight="tall">
                <strong>ROI Impact:</strong> Reduce physical prototyping by 60%, accelerate design cycles from months to weeks, and eliminate capital expenses on local HPC infrastructure. One simulation replaces dozens of expensive lab tests.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Solve Your Multiphase Acoustics Problems?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Start your own high-fidelity simulation on NumericalAI's cloud platform. No installation, no infrastructure management — just results.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default MultiphaseAcousticsBubbleScreen;